import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useHotel } from "../../contexts/HotelContext";
import {
  useSubscriptionPlans,
  useUpdatePlanModules,
  useUpdatePlanBrands,
  useSyncStripeProducts,
  useBrands,
} from "../../hooks/useSubscriptionPlans";
import { SubscriptionPlan, Brand } from "../../services/accountService";
import BrandSelector from "../../components/BrandSelector";
import {
  MODULE_STRUCTURE,
  MODULE_DISPLAY_NAMES,
  hasModuleAccess,
  type ModuleName,
} from "../../lib/modules";
import Layout from "../../components/Layout";

const SubscriptionPlansManagement: React.FC = () => {
  const { user } = useHotel();
  const navigate = useNavigate();

  const [editingPlan, setEditingPlan] = useState<SubscriptionPlan | null>(null);

  const { data: plans, isLoading, error } = useSubscriptionPlans();
  const { data: brands } = useBrands();
  const updateModulesMutation = useUpdatePlanModules();
  const updateBrandsMutation = useUpdatePlanBrands();
  const syncMutation = useSyncStripeProducts();

  // Redirect non-admin users
  if (user?.role !== "admin") {
    navigate("/");
    return null;
  }

  const handleSyncStripe = () => {
    syncMutation.mutate(undefined, {
      onSuccess: (response) => {
        alert(
          `Synchronizacja zakończona! Zsynchronizowano ${response.productsProcessed} produktów i ${response.pricesProcessed} cen.`
        );
      },
      onError: (error) => {
        alert(`Błąd synchronizacji: ${error.message}`);
      },
    });
  };

  const handleModuleToggle = (
    planId: string,
    module: ModuleName,
    enabled: boolean
  ) => {
    const plan = plans?.find((p) => p.id === planId);
    if (!plan) return;

    const newModules = enabled
      ? [...plan.includedModules, module]
      : plan.includedModules.filter((m) => m !== module);

    // Prevent removing the last module
    if (newModules.length === 0) {
      alert("Każdy plan musi mieć przypisany przynajmniej jeden moduł");
      return;
    }

    updateModulesMutation.mutate({ planId, modules: newModules });
  };

  const handleBrandToggle = (
    planId: string,
    brandId: string,
    selected: boolean
  ) => {
    const plan = plans?.find((p) => p.id === planId);
    if (!plan) return;

    const newBrandIds = selected
      ? [...plan.brands.map((b) => b.id), brandId]
      : plan.brands.map((b) => b.id).filter((id) => id !== brandId);

    // Prevent removing the last brand
    if (newBrandIds.length === 0) {
      alert("Każdy plan musi być przypisany do przynajmniej jednego brandu");
      return;
    }

    updateBrandsMutation.mutate({ planId, brandIds: newBrandIds });
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <h3 className="text-red-800 font-medium">
            Błąd ładowania planów subskrypcji
          </h3>
          <p className="text-red-600 text-sm mt-1">
            {error instanceof Error ? error.message : "Nieznany błąd"}
          </p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-neutral-900">
              Plany Subskrypcji
            </h1>
            <p className="text-neutral-600 mt-1">
              Zarządzaj planami subskrypcji i przypisanymi modułami
            </p>
          </div>
          <button
            onClick={handleSyncStripe}
            disabled={syncMutation.isPending}
            className="bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center space-x-2"
          >
            <svg
              className={`w-4 h-4 ${syncMutation.isPending ? "animate-spin" : ""}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
            <span>
              {syncMutation.isPending
                ? "Synchronizacja..."
                : "Synchronizuj Stripe"}
            </span>
          </button>
        </div>

        {/* Plans Table */}
        <div className="bg-white shadow-sm rounded-lg border border-neutral-200">
          <div className="px-6 py-4 border-b border-neutral-200">
            <h2 className="text-lg font-semibold text-neutral-900">
              Lista Planów ({plans?.length || 0})
            </h2>
          </div>

          {plans && plans.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-neutral-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                      Nazwa
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                      Stripe ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                      Opis
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                      Brandy
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                      Przypisane Moduły
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-neutral-500 uppercase tracking-wider">
                      Akcje
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-neutral-200">
                  {plans.map((plan) => (
                    <tr key={plan.id} className="hover:bg-neutral-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="font-medium text-neutral-900">
                          {plan.name}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-neutral-600 font-mono text-sm">
                          {plan.stripeProductId}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="max-w-xs">
                          {plan.description ? (
                            <div className="text-neutral-600 text-sm truncate">
                              {plan.description}
                            </div>
                          ) : (
                            <div className="text-neutral-400 text-xs italic">
                              Brak opisu
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <BrandDisplay brands={plan.brands} />
                      </td>
                      <td className="px-6 py-4">
                        <ModuleDisplay includedModules={plan.includedModules} />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            plan.status === "active"
                              ? "bg-green-100 text-green-800"
                              : "bg-neutral-100 text-neutral-500"
                          }`}
                        >
                          {plan.status === "active" ? "Aktywny" : plan.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => setEditingPlan(plan)}
                          className="text-primary-600 hover:text-primary-900 transition-colors"
                        >
                          Edytuj
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="px-6 py-12 text-center">
              <div className="text-neutral-400 text-lg mb-2">
                Brak planów subskrypcji
              </div>
              <p className="text-neutral-500 text-sm mb-4">
                Zsynchronizuj produkty ze Stripe, aby rozpocząć zarządzanie
                planami
              </p>
              <button
                onClick={handleSyncStripe}
                disabled={syncMutation.isPending}
                className="bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white px-4 py-2 rounded-lg font-medium transition-colors"
              >
                {syncMutation.isPending
                  ? "Synchronizacja..."
                  : "Synchronizuj Stripe"}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Edit Plan Modal */}
      {editingPlan && brands && (
        <EditPlanModal
          plan={editingPlan}
          availableBrands={brands}
          onClose={() => setEditingPlan(null)}
          onModuleToggle={(module, enabled) =>
            handleModuleToggle(editingPlan.id, module, enabled)
          }
          onBrandToggle={(brandId, selected) =>
            handleBrandToggle(editingPlan.id, brandId, selected)
          }
          isLoading={
            updateModulesMutation.isPending || updateBrandsMutation.isPending
          }
        />
      )}
    </Layout>
  );
};

// Edit Plan Modal Component
const EditPlanModal: React.FC<{
  plan: SubscriptionPlan;
  availableBrands: Brand[];
  onClose: () => void;
  onModuleToggle: (module: ModuleName, enabled: boolean) => void;
  onBrandToggle: (brandId: string, selected: boolean) => void;
  isLoading: boolean;
}> = ({
  plan,
  availableBrands,
  onClose,
  onModuleToggle,
  onBrandToggle,
  isLoading,
}) => {
  // Local state for enabled modules with optimistic updates
  const [localIncludedModules, setLocalIncludedModules] = useState<string[]>(
    plan.includedModules
  );

  // Update local state when plan data changes
  React.useEffect(() => {
    setLocalIncludedModules(plan.includedModules);
  }, [plan.includedModules]);

  const handleModuleToggleWithOptimism = (
    module: ModuleName,
    enabled: boolean
  ) => {
    // Optimistic update - update local state immediately
    setLocalIncludedModules((prev) =>
      enabled ? [...prev, module] : prev.filter((m) => m !== module)
    );

    // Call the actual mutation
    onModuleToggle(module, enabled);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg max-w-3xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-neutral-200">
          <h3 className="text-lg font-semibold text-neutral-900">
            Edytuj Plan: {plan.name}
          </h3>
        </div>

        <div className="p-6 space-y-6">
          {/* Plan Info (read-only) */}
          <div className="bg-neutral-50 p-4 rounded-md space-y-2">
            <div>
              <span className="text-sm font-medium text-neutral-700">
                Nazwa planu:
              </span>
              <span className="ml-2 text-sm text-neutral-900">{plan.name}</span>
            </div>
            <div>
              <span className="text-sm font-medium text-neutral-700">
                Stripe Product ID:
              </span>
              <span className="ml-2 text-sm font-mono text-neutral-900">
                {plan.stripeProductId}
              </span>
            </div>
            {plan.description && (
              <div>
                <span className="text-sm font-medium text-neutral-700">
                  Opis:
                </span>
                <span className="ml-2 text-sm text-neutral-600">
                  {plan.description}
                </span>
              </div>
            )}
          </div>

          {/* Brands Section */}
          <div>
            <BrandSelector
              availableBrands={availableBrands}
              selectedBrandIds={plan.brands.map((b) => b.id)}
              onBrandToggle={onBrandToggle}
              isLoading={isLoading}
            />
          </div>

          {/* Modules Section */}
          <div>
            <h4 className="text-sm font-medium text-neutral-900 mb-4">
              Przypisane Moduły
            </h4>
            <ModuleSelector
              includedModules={localIncludedModules}
              onModuleToggle={handleModuleToggleWithOptimism}
              isLoading={isLoading}
            />
          </div>
        </div>

        <div className="px-6 py-4 border-t border-neutral-200 flex justify-end">
          <button
            onClick={onClose}
            disabled={isLoading}
            className="px-4 py-2 text-neutral-600 hover:text-neutral-800 font-medium transition-colors"
          >
            Zamknij
          </button>
        </div>
      </div>
    </div>
  );
};

// Module Selector Component (reused from TenantManagement pattern)
const ModuleSelector: React.FC<{
  includedModules: string[];
  onModuleToggle: (module: ModuleName, enabled: boolean) => void;
  isLoading: boolean;
}> = ({ includedModules, onModuleToggle, isLoading }) => {
  // Check if a module would be the last one if unchecked
  const wouldBeLastModule = (moduleToCheck: string): boolean => {
    // Count how many modules would remain after removing this one
    const remainingModules = includedModules.filter((m) => m !== moduleToCheck);
    return remainingModules.length === 0;
  };

  const handleWildcardToggle = (categoryKey: string, enabled: boolean) => {
    const category =
      MODULE_STRUCTURE[categoryKey as keyof typeof MODULE_STRUCTURE];
    if (!category || !category.wildcard) return;

    if (enabled) {
      // Enable wildcard
      onModuleToggle(category.wildcard as ModuleName, true);
    } else {
      // Check if this would leave no modules
      if (wouldBeLastModule(category.wildcard)) {
        alert("Każdy plan musi mieć przypisany przynajmniej jeden moduł");
        return;
      }

      // Disable wildcard and all individual modules
      onModuleToggle(category.wildcard as ModuleName, false);
      category.modules.forEach((module) => {
        if (includedModules.includes(module)) {
          onModuleToggle(module as ModuleName, false);
        }
      });
    }
  };

  const handleIndividualModuleToggle = (module: string, enabled: boolean) => {
    const [categoryKey] = module.split(":");
    const category =
      MODULE_STRUCTURE[categoryKey as keyof typeof MODULE_STRUCTURE];

    if (enabled) {
      // If wildcard is enabled, disable it first
      if (category.wildcard && includedModules.includes(category.wildcard)) {
        onModuleToggle(category.wildcard as ModuleName, false);
      }
      onModuleToggle(module as ModuleName, true);
    } else {
      // Check if this would leave no modules
      if (wouldBeLastModule(module)) {
        alert("Każdy plan musi mieć przypisany przynajmniej jeden moduł");
        return;
      }
      onModuleToggle(module as ModuleName, false);
    }
  };

  const isWildcardEnabled = (categoryKey: string) => {
    const category =
      MODULE_STRUCTURE[categoryKey as keyof typeof MODULE_STRUCTURE];
    if (!category.wildcard) return false;
    return includedModules.includes(category.wildcard);
  };

  const isIndividualModuleEnabled = (module: string) => {
    return hasModuleAccess(includedModules, module);
  };

  return (
    <div className="space-y-4">
      {Object.entries(MODULE_STRUCTURE).map(([categoryKey, category]) => {
        const wildcardEnabled = isWildcardEnabled(categoryKey);
        const isWildcardLastModule = wouldBeLastModule(category.wildcard);

        return (
          <div key={categoryKey} className="space-y-2">
            {/* Category wildcard option */}
            <label
              className="flex items-center space-x-3 p-2 bg-neutral-50 rounded-md"
              title={
                wildcardEnabled && isWildcardLastModule
                    ? "Nie można odznaczyć ostatniego modułu"
                    : ""
                }
              >
                <input
                  type="checkbox"
                  checked={wildcardEnabled}
                  onChange={(e) =>
                    handleWildcardToggle(categoryKey, e.target.checked)
                  }
                  disabled={
                    isLoading ||
                    (wildcardEnabled && isWildcardLastModule)
                  }
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-neutral-300 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                />
                <span
                  className={`text-sm font-medium ${
                    wildcardEnabled && isWildcardLastModule
                      ? "text-neutral-400"
                      : "text-neutral-700"
                  }`}
                >
                  {category.label} (wszystkie) *
                </span>
              </label>

            {/* Individual modules */}
            <div className="ml-6 space-y-2">
              {category.modules.map((module) => {
                const displayName = MODULE_DISPLAY_NAMES[module] || module;
                const isEnabled = isIndividualModuleEnabled(module);
                const isDisabled = wildcardEnabled || isLoading;

                const isLastModule = isEnabled && wouldBeLastModule(module);
                const shouldDisable = isDisabled || isLastModule;

                return (
                  <label
                    key={module}
                    className="flex items-center space-x-3"
                    title={
                      isLastModule
                        ? "Nie można odznaczyć ostatniego modułu"
                        : ""
                    }
                  >
                    <input
                      type="checkbox"
                      checked={isEnabled}
                      onChange={(e) =>
                        handleIndividualModuleToggle(module, e.target.checked)
                      }
                      disabled={shouldDisable}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-neutral-300 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                    />
                    <span
                      className={`text-sm ${shouldDisable ? "text-neutral-400" : "text-neutral-700"}`}
                    >
                      {displayName}
                    </span>
                  </label>
                );
              })}
            </div>
          </div>
        );
      })}
      <div className="text-xs text-neutral-500 mt-2 space-y-1">
        <div>
          * Wybór opcji "wszystkie" automatycznie włącza wszystkie podmoduły w
          kategorii
        </div>
        <div className="text-amber-600 font-medium">
          ⚠️ Każdy plan musi mieć przypisany przynajmniej jeden moduł
        </div>
      </div>
    </div>
  );
};

// Module Display Component
const ModuleDisplay: React.FC<{ includedModules: string[] }> = ({
  includedModules,
}) => {
  // Ensure includedModules is always an array
  const safeIncludedModules = includedModules || [];

  const getModuleDisplayInfo = () => {
    const moduleInfo: Array<{
      name: string;
      type: "category" | "wildcard" | "specific";
      enabled: boolean;
    }> = [];

    // Check each category
    Object.entries(MODULE_STRUCTURE).forEach(([categoryKey, category]) => {
      const wildcardModule = category.wildcard;
      const categoryModules = category.modules;

      // Check if wildcard is enabled (only if wildcard exists)
      if (wildcardModule && safeIncludedModules.includes(wildcardModule)) {
        moduleInfo.push({
          name: `${category.label} (wszystkie)`,
          type: "wildcard",
          enabled: true,
        });
      } else {
        // Check individual modules
        const enabledInCategory = categoryModules.filter((mod) =>
          hasModuleAccess(safeIncludedModules, mod)
        );

        if (enabledInCategory.length > 0) {
          if (enabledInCategory.length === categoryModules.length) {
            // All modules enabled - show as category
            moduleInfo.push({
              name: category.label,
              type: "category",
              enabled: true,
            });
          } else {
            // Some modules enabled - show specific ones
            enabledInCategory.forEach((mod) => {
              const displayName = MODULE_DISPLAY_NAMES[mod] || mod;
              moduleInfo.push({
                name: displayName,
                type: "specific",
                enabled: true,
              });
            });
          }
        }
      }
    });

    return moduleInfo;
  };

  const moduleInfo = getModuleDisplayInfo();

  return (
    <div className="flex flex-wrap gap-1">
      {moduleInfo.map((module, index) => (
        <span
          key={index}
          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
            module.enabled
              ? "bg-green-100 text-green-800"
              : "bg-neutral-100 text-neutral-500"
          }`}
        >
          {module.name}
          {module.type === "wildcard" && <span className="ml-1">*</span>}
        </span>
      ))}
      {moduleInfo.length === 0 && (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
          Brak modułów (skonfiguruj!)
        </span>
      )}
    </div>
  );
};

// Brand Display Component
const BrandDisplay: React.FC<{ brands: Brand[] }> = ({ brands }) => {
  const safeBrands = brands || [];

  return (
    <div className="flex flex-wrap gap-1">
      {safeBrands.length === 0 ? (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
          Brak brandów (skonfiguruj!)
        </span>
      ) : (
        safeBrands.map((brand) => (
          <span
            key={brand.id}
            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800"
          >
            {brand.displayName}
          </span>
        ))
      )}
    </div>
  );
};

export default SubscriptionPlansManagement;
